// "Did it actually arrive?" — a real send to a real inbox.
//
// Verification proves the mailbox can authenticate and receive its own mail;
// this proves it reaches somewhere else, which is where spam filters and
// sender-policy problems show up. Only a VERIFIED transport may be tested
// (plan §5.5) — the server answers 409 otherwise.

import { useState } from 'react';
import { Button, Input, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import type { EmailTransportClient } from '@/services/email-transport.service';
import type { EmailTransportConfigMasked } from '@bbpos/types';

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function formatStamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

export function SendTestEmailCard({
  config,
  client,
  canEdit,
  defaultTo,
  onTested,
}: {
  config: EmailTransportConfigMasked;
  client: EmailTransportClient;
  canEdit: boolean;
  /** Usually the signed-in user's login email — where the server sends it anyway. */
  defaultTo?: string;
  onTested?: () => void | Promise<void>;
}) {
  const { toast } = useToast();
  const [to, setTo] = useState(defaultTo ?? '');
  const [sending, setSending] = useState(false);

  const disabled = !canEdit || config.verification_status !== 'VERIFIED';
  const recipient = to.trim();
  const recipientValid = !recipient || EMAIL_REGEX.test(recipient);

  const lastTestedAt = formatStamp(config.last_tested_at);

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await client.sendTest(recipient || undefined);
      if (result.ok) {
        toast({
          variant: 'success',
          title: 'Test email sent',
          description: recipient ? `Check ${recipient}` : 'Check your inbox',
        });
      } else {
        toast({ variant: 'warning', title: 'Test failed', description: result.error });
      }
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Test failed',
        description: err instanceof ApiError ? err.message : 'Could not send the test email',
      });
    } finally {
      setSending(false);
      await onTested?.();
    }
  };

  return (
    <div className={cn(GLASS_FORM, 'animate-fade-in space-y-4 p-6')} data-testid="send-test-email-card">
      <div>
        <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">Send a test email</p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
          Sends a real message through this account so you can confirm it lands in the inbox, not
          the spam folder.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <Input
            label="Send to"
            type="email"
            placeholder="you@example.com"
            value={to}
            onChange={setTo}
            error={recipientValid ? undefined : 'Enter a valid email address'}
            disabled={disabled}
            name="test_to"
          />
        </div>
        <Button
          variant="secondary"
          loading={sending}
          disabled={disabled || !recipientValid}
          onClick={() => void handleSend()}
          data-testid="send-test-email"
        >
          Send test
        </Button>
      </div>

      {config.verification_status !== 'VERIFIED' && (
        <p className="text-xs text-warning-600 dark:text-warning-400">
          Verify the sender before sending a test.
        </p>
      )}

      {lastTestedAt && (
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-xs',
            config.last_test_status === 'OK'
              ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300'
              : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
          )}
        >
          <span className="font-semibold">
            {config.last_test_status === 'OK' ? 'Last test succeeded' : 'Last test failed'}
          </span>{' '}
          · {lastTestedAt}
          {config.last_test_error && <span className="block break-words mt-1 font-mono">{config.last_test_error}</span>}
        </div>
      )}
    </div>
  );
}
